import { useEffect, useMemo, useState } from "react";
import {
  Outlet,
  NavLink,
  useNavigate,
  Link,
  useLocation,
} from "react-router-dom";
import { useUsers } from "../../hooks/useUsers";
import {
  clearSessionUser,
  getDefaultPathForRole,
  mustForcePasswordChange,
  readSessionUser,
  restoreSessionFromApi,
  type SessionUser,
} from "../../lib/authSession";

interface NavItem {
  to: string;
  icon: string;
  label: string;
  badge?: string;
  external?: boolean;
  roles?: SessionUser["rol"][];
}

interface NavGroup {
  title: string;
  items: NavItem[];
}

const NAV_GROUPS: NavGroup[] = [
  {
    title: "Satış & Müşteri",
    items: [
      {
        to: "/panel/hayvanlar",
        icon: "ri-database-2-line",
        label: "Hayvanlar",
        roles: ["Admin", "Muhasebe"],
      },
      {
        to: "/panel/musteriler",
        icon: "ri-group-line",
        label: "Müşteriler",
        roles: ["Admin", "Muhasebe"],
      },
      {
        to: "/panel/satislar",
        icon: "ri-shopping-bag-3-line",
        label: "Satışlar",
        roles: ["Admin", "Muhasebe"],
      },
      {
        to: "/panel/hisse-satislari",
        icon: "ri-ticket-2-line",
        label: "Hisse Satışları",
        roles: ["Admin", "Muhasebe"],
      },
      {
        to: "/panel/odeme-al",
        icon: "ri-coins-line",
        label: "Ödeme Al",
        roles: ["Admin", "Muhasebe"],
      },
      {
        to: "/panel/odemeler",
        icon: "ri-receipt-line",
        label: "Ödemeler",
        roles: ["Admin", "Muhasebe"],
      },
      {
        to: "/panel/havale",
        icon: "ri-bank-card-line",
        label: "Havale",
        roles: ["Admin", "Muhasebe"],
      },
    ],
  },
  {
    title: "Operasyon",
    items: [
      {
        to: "/panel/kesim-planlama",
        icon: "ri-calendar-schedule-line",
        label: "Kesim Planlama",
        badge: "Pasif",
        roles: ["Admin", "Muhasebe"],
      },
      {
        to: "/panel/kesim-alani",
        icon: "ri-scissors-2-line",
        label: "Kesim Alanı",
        external: true,
        roles: ["Admin", "Muhasebe", "Kesim Alanı"],
      },
      {
        to: "/panel/tasima",
        icon: "ri-truck-line",
        label: "Taşıma",
        roles: ["Admin", "Muhasebe"],
      },
      {
        to: "/panel/tasiyici-tasima",
        icon: "ri-truck-line",
        label: "Taşıyıcı Ekranı",
        external: true,
        roles: ["Admin", "Muhasebe", "Taşıyıcı"],
      },
      {
        to: "/panel/tv-ekrani",
        icon: "ri-tv-2-line",
        label: "TV Ekranı",
        external: true,
        roles: ["Admin", "Muhasebe", "Kesim Alanı"],
      },
      {
        to: "/panel/havale-tv",
        icon: "ri-tv-line",
        label: "Havale TV",
        external: true,
        roles: ["Admin", "Muhasebe"],
      },
    ],
  },
  {
    title: "Finans & Yönetim",
    items: [
      {
        to: "/panel/raporlar",
        icon: "ri-bar-chart-box-line",
        label: "Raporlar",
        roles: ["Admin", "Muhasebe"],
      },
      {
        to: "/panel/loglar",
        icon: "ri-file-list-3-line",
        label: "LOG",
        roles: ["Admin", "Muhasebe"],
      },
      {
        to: "/panel/tamamlanan",
        icon: "ri-checkbox-circle-line",
        label: "Tamamlananlar",
        roles: ["Admin", "Muhasebe"],
      },
      {
        to: "/panel/gelir-gider",
        icon: "ri-wallet-3-line",
        label: "Gelir / Gider",
        roles: ["Admin", "Muhasebe"],
      },
      {
        to: "/panel/stok",
        icon: "ri-archive-drawer-line",
        label: "Stok",
        roles: ["Admin", "Muhasebe"],
      },
      {
        to: "/panel/kullanici-yonetimi",
        icon: "ri-user-settings-line",
        label: "Kullanıcı Yönetimi",
        roles: ["Admin", "Muhasebe"],
      },
      {
        to: "/panel/site-yonetimi",
        icon: "ri-window-line",
        label: "Site Yönetimi",
        roles: ["Admin"],
      },
      {
        to: "/panel/sms-ayarlari",
        icon: "ri-settings-3-line",
        label: "SMS Ayarları",
        roles: ["Admin", "Muhasebe"],
      },
      {
        to: "/panel/sms-musteri",
        icon: "ri-message-2-line",
        label: "SMS Müşteri",
        roles: ["Admin", "Muhasebe"],
      },
    ],
  },
];

function getInitials(name: string) {
  return name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
}

function isItemAllowed(item: NavItem, role: SessionUser["rol"]) {
  if (!item.roles || item.roles.length === 0) return true;
  return item.roles.includes(role);
}

function buildVisibleGroups(role: SessionUser["rol"]) {
  return NAV_GROUPS.map((group) => ({
    ...group,
    items: group.items.filter((item) => isItemAllowed(item, role)),
  })).filter((group) => group.items.length > 0);
}

function SidebarContent({
  onClose,
  user,
}: {
  onClose?: () => void;
  user: SessionUser;
}) {
  const navigate = useNavigate();

  const visibleGroups = useMemo(
    () => buildVisibleGroups(user.rol),
    [user.rol]
  );

  function handleLogout() {
    clearSessionUser();
    navigate("/giris");
  }

  return (
    <div className="flex flex-col h-full bg-farm-warm border-r border-farm-border">
      <div className="flex items-center gap-2.5 px-4 h-14 border-b border-farm-border flex-shrink-0">
        <div className="w-8 h-8 flex items-center justify-center rounded-full overflow-hidden bg-farm-earth-light flex-shrink-0">
          <img
            src="https://public.readdy.ai/ai/img_res/73cf5855-ea32-42f4-956b-881be407de25.png"
            alt="Evren Çiftliği"
            className="w-7 h-7 object-contain"
          />
        </div>

        <div className="min-w-0">
          <p className="font-heading font-bold text-sm text-farm-brown leading-none truncate">
            Evren Çiftliği
          </p>
          <p className="font-body text-[10px] text-farm-muted leading-none mt-1">
            Yönetim Paneli
          </p>
        </div>

        {onClose && (
          <button
            onClick={onClose}
            className="ml-auto w-8 h-8 flex items-center justify-center rounded-lg text-farm-muted hover:bg-farm-border cursor-pointer transition-colors flex-shrink-0"
          >
            <i className="ri-close-line text-lg" />
          </button>
        )}
      </div>

      <div className="px-3 pt-3">
        <div className="rounded-farm border border-farm-border bg-white px-3 py-3">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 flex items-center justify-center rounded-full bg-farm-earth-pale text-farm-earth font-bold text-xs flex-shrink-0">
              {getInitials(user.adSoyad)}
            </div>

            <div className="min-w-0">
              <p className="font-body text-xs font-semibold text-farm-brown truncate leading-none">
                {user.adSoyad}
              </p>
              <p className="font-body text-[10px] text-farm-muted leading-none mt-1">
                {user.rol}
              </p>
            </div>
          </div>
        </div>
      </div>

      <nav className="flex-1 overflow-y-auto py-3 px-2 space-y-4">
        {visibleGroups.map((group) => (
          <div key={group.title}>
            <p className="px-2 mb-1 font-body text-[10px] font-semibold text-farm-muted/60 uppercase tracking-widest">
              {group.title}
            </p>

            <div className="space-y-0.5">
              {group.items.map((item) =>
                item.external ? (
                  <Link
                    key={item.to}
                    to={item.to}
                    onClick={onClose}
                    className="flex items-center gap-2.5 px-3 py-2 rounded-lg text-farm-muted hover:bg-farm-cream hover:text-farm-brown transition-colors cursor-pointer group"
                  >
                    <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                      <i className={`${item.icon} text-sm`} />
                    </div>

                    <span className="font-body text-xs font-medium truncate">
                      {item.label}
                    </span>

                    {item.badge && (
                      <span className="ml-auto inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] font-semibold bg-farm-earth-pale text-farm-earth border border-farm-earth/20">
                        {item.badge}
                      </span>
                    )}

                    {!item.badge && (
                      <div className="ml-auto w-3 h-3 flex items-center justify-center opacity-0 group-hover:opacity-40 flex-shrink-0">
                        <i className="ri-external-link-line text-[10px]" />
                      </div>
                    )}
                  </Link>
                ) : (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    onClick={onClose}
                    className={({ isActive }) =>
                      `flex items-center gap-2.5 px-3 py-2 rounded-lg transition-colors cursor-pointer ${
                        isActive
                          ? "bg-farm-earth text-white"
                          : "text-farm-muted hover:bg-farm-cream hover:text-farm-brown"
                      }`
                    }
                  >
                    {({ isActive }) => (
                      <>
                        <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                          <i className={`${item.icon} text-sm`} />
                        </div>

                        <span
                          className={`font-body text-xs font-medium truncate ${
                            isActive ? "font-semibold" : ""
                          }`}
                        >
                          {item.label}
                        </span>

                        {item.badge && (
                          <span
                            className={`ml-auto inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] font-semibold border ${
                              isActive
                                ? "bg-white/20 text-white border-white/20"
                                : "bg-farm-earth-pale text-farm-earth border-farm-earth/20"
                            }`}
                          >
                            {item.badge}
                          </span>
                        )}
                      </>
                    )}
                  </NavLink>
                )
              )}
            </div>
          </div>
        ))}

        <div>
          <div className="space-y-0.5">
            <Link
              to="/sifre-degistir"
              onClick={onClose}
              className="flex items-center gap-2.5 px-3 py-2 rounded-lg text-farm-muted hover:bg-farm-cream hover:text-farm-brown transition-colors cursor-pointer"
            >
              <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                <i className="ri-lock-password-line text-sm" />
              </div>
              <span className="font-body text-xs font-medium">Şifre Değiştir</span>
            </Link>

            <Link
              to="/"
              onClick={onClose}
              className="flex items-center gap-2.5 px-3 py-2 rounded-lg text-farm-muted hover:bg-farm-cream hover:text-farm-brown transition-colors cursor-pointer"
            >
              <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                <i className="ri-home-line text-sm" />
              </div>
              <span className="font-body text-xs font-medium">Ana Sayfa</span>
            </Link>
          </div>
        </div>
      </nav>

      <div className="border-t border-farm-border px-3 py-3 flex-shrink-0">
        <button
          onClick={handleLogout}
          className="w-full h-10 flex items-center justify-center gap-2 rounded-lg border border-red-200 bg-red-50 text-red-600 text-xs font-semibold hover:bg-red-500 hover:text-white transition-colors"
        >
          <i className="ri-logout-box-r-line text-sm" />
          Çıkış Yap
        </button>
      </div>
    </div>
  );
}

export default function PanelLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { users } = useUsers();

  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.localStorage.getItem("ec.panel.sidebarCollapsed.v1") === "1";
  });
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [authReady, setAuthReady] = useState(false);
  const [sessionUser, setSessionUser] = useState<SessionUser | null>(() =>
    readSessionUser()
  );

  useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem("ec.panel.sidebarCollapsed.v1", sidebarCollapsed ? "1" : "0");
  }, [sidebarCollapsed]);

  useEffect(() => {
    let active = true;

    async function boot() {
      const localUser = readSessionUser();

      if (!localUser) {
        clearSessionUser();
        if (active) {
          navigate("/giris", { replace: true });
        }
        return;
      }

      if (active) {
        setSessionUser(localUser);
      }

      const restored = await restoreSessionFromApi();

      if (!active) return;

      if (!restored) {
        clearSessionUser();
        navigate("/giris", { replace: true });
        return;
      }

      setSessionUser(restored);
      setAuthReady(true);
    }

    void boot();

    return () => {
      active = false;
    };
  }, [navigate]);

  const currentUser = useMemo(() => {
    if (!sessionUser) return null;
    return users.find((u) => u.id === sessionUser.id) ?? null;
  }, [sessionUser, users]);

  useEffect(() => {
    if (!authReady || !sessionUser) return;

    const effectiveUser = currentUser ?? sessionUser;

    if ("durum" in effectiveUser && effectiveUser.durum !== "Aktif") {
      clearSessionUser();
      navigate("/giris", { replace: true });
      return;
    }

    if (mustForcePasswordChange(effectiveUser)) {
      navigate("/sifre-degistir", { replace: true });
      return;
    }

    if (effectiveUser.rol === "Müşteri") {
      navigate("/musteri-panel", { replace: true });
      return;
    }

    if (effectiveUser.rol === "Kesim Alanı") {
      navigate("/panel/kesim-alani", { replace: true });
      return;
    }

    if (effectiveUser.rol === "Taşıyıcı") {
      navigate("/panel/tasiyici-tasima", { replace: true });
      return;
    }

    if (effectiveUser.rol === "Sıra Operatörü") {
      navigate("/panel/istasyon/sira", { replace: true });
      return;
    }

    if (effectiveUser.rol === "Kesim Operatörü") {
      navigate("/panel/istasyon/kesim", { replace: true });
      return;
    }

    if (effectiveUser.rol === "Parçalama Operatörü") {
      navigate("/panel/istasyon/parcalama", { replace: true });
      return;
    }

    if (effectiveUser.rol === "Tamamlama Operatörü") {
      navigate("/panel/istasyon/tamamlama", { replace: true });
      return;
    }

    const defaultPath = getDefaultPathForRole(effectiveUser.rol);
    if (location.pathname === "/panel" || location.pathname === "/panel/") {
      navigate(defaultPath, { replace: true });
    }
  }, [authReady, sessionUser, currentUser, navigate, location.pathname]);

  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  if (!authReady || !sessionUser) {
    return null;
  }

  const effectiveUser = currentUser ?? sessionUser;

  if (
    effectiveUser.rol === "Müşteri" ||
    effectiveUser.rol === "Kesim Alanı" ||
    effectiveUser.rol === "Taşıyıcı" ||
    effectiveUser.rol === "Sıra Operatörü" ||
    effectiveUser.rol === "Kesim Operatörü" ||
    effectiveUser.rol === "Parçalama Operatörü" ||
    effectiveUser.rol === "Tamamlama Operatörü"
  ) {
    return null;
  }

  const sidebarUser: SessionUser = {
    id: effectiveUser.id,
    adSoyad: effectiveUser.adSoyad,
    rol: effectiveUser.rol,
    customerId: effectiveUser.customerId,
    forcePasswordChange: effectiveUser.forcePasswordChange,
    smsOperationalOptIn: effectiveUser.smsOperationalOptIn,
    smsMarketingOptIn: effectiveUser.smsMarketingOptIn,
  };

  return (
    <div className="flex h-screen overflow-hidden bg-farm-cream">
      <aside className={`hidden lg:flex flex-col ${sidebarCollapsed ? "w-16" : "w-56"} flex-shrink-0 h-full overflow-visible relative transition-all duration-200`}>
        <div className="h-full overflow-hidden">
          <SidebarContent user={sidebarUser} />
        </div>
        <button
          type="button"
          onClick={() => setSidebarCollapsed((prev) => !prev)}
          className="absolute -right-3 top-20 z-40 w-7 h-7 rounded-full border border-farm-border bg-white text-farm-brown shadow-sm hover:bg-farm-cream flex items-center justify-center"
          title={sidebarCollapsed ? "Menüyü aç" : "Menüyü daralt"}
        >
          <i className={`${sidebarCollapsed ? "ri-arrow-right-s-line" : "ri-arrow-left-s-line"} text-base`} />
        </button>
      </aside>

      {drawerOpen && (
        <div
          className="lg:hidden fixed inset-0 z-50 flex"
          onClick={() => setDrawerOpen(false)}
        >
          <div className="w-64 h-full" onClick={(e) => e.stopPropagation()}>
            <SidebarContent
              user={sidebarUser}
              onClose={() => setDrawerOpen(false)}
            />
          </div>
          <div className="flex-1 bg-black/40" />
        </div>
      )}

      <div className="flex flex-col flex-1 min-w-0 overflow-hidden">
        <div className="lg:hidden flex items-center gap-3 px-3 h-12 bg-farm-warm border-b border-farm-border flex-shrink-0">
          <button
            onClick={() => setDrawerOpen(true)}
            className="w-8 h-8 flex items-center justify-center rounded-lg text-farm-muted hover:bg-farm-cream cursor-pointer transition-colors"
          >
            <i className="ri-menu-line text-lg" />
          </button>

          <div className="flex items-center gap-2 flex-1 min-w-0">
            <div className="w-6 h-6 flex items-center justify-center rounded-full overflow-hidden bg-farm-earth-light flex-shrink-0">
              <img
                src="https://public.readdy.ai/ai/img_res/73cf5855-ea32-42f4-956b-881be407de25.png"
                alt="Evren Çiftliği"
                className="w-5 h-5 object-contain"
              />
            </div>

            <span className="font-heading font-semibold text-xs text-farm-brown truncate">
              Evren Çiftliği
            </span>
          </div>

          <Link
            to={getDefaultPathForRole(sidebarUser.rol)}
            className="h-8 px-2.5 inline-flex items-center justify-center rounded-lg bg-farm-cream border border-farm-border text-[10px] font-semibold text-farm-brown whitespace-nowrap"
          >
            Panel
          </Link>
        </div>

        <main className="flex-1 overflow-auto">
          <div key={location.pathname} className="min-h-full">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}